import React from 'react';
import PropTypes from 'prop-types';
import {
  VARIANT_STYLES,
  SIZE_STYLES,
  ALIGN_STYLES,
  getTabStyle,
  getRenderTabStyle,
} from './constants';

/**
 * TabsV2 — A controlled, accessible tab navigation component.
 *
 * This is a fully controlled component — the parent owns the active tab state
 * and receives change notifications via `onChange`. It renders only the tab bar;
 * content rendering is the parent's responsibility.
 *
 * @example
 * // Basic usage
 * const [activeTab, setActiveTab] = useState('order');
 *
 * <TabsV2
 *   tabs={[
 *     { key: 'order', label: 'Order Level' },
 *     { key: 'user', label: 'User Level' },
 *   ]}
 *   activeKey={activeTab}
 *   onChange={setActiveTab}
 * />
 *
 * @example
 * // With variant, size, and disabled tabs
 * <TabsV2
 *   tabs={tabs}
 *   activeKey={activeTab}
 *   onChange={setActiveTab}
 *   variant="pill"
 *   size="lg"
 *   disabled={['user']}
 * />
 *
 * @example
 * // Custom tab rendering via renderTab
 * <TabsV2
 *   tabs={tabs}
 *   activeKey={activeTab}
 *   onChange={setActiveTab}
 *   renderTab={(tab, isActive) => (
 *     <div className={isActive ? 'tw-font-bold' : ''}>
 *       {tab.icon} {tab.label}
 *     </div>
 *   )}
 * />
 *
 * @param {Object}   props
 * @param {Array<{ key: string, label: string }>} props.tabs - Tab definitions. Each object must have a unique `key` and a display `label`.
 * @param {string}   props.activeKey - The `key` of the currently active tab. Must match one of the `tabs[].key` values.
 * @param {function} props.onChange - Callback fired when a tab is clicked. Receives the clicked tab's `key` as the only argument.
 * @param {'line'|'pill'|'enclosed'} [props.variant='line'] - Visual style. `line`: underline indicator. `pill`: rounded background. `enclosed`: card-style with shadow.
 * @param {'sm'|'md'|'lg'} [props.size='md'] - Controls font size and padding of each tab.
 * @param {'left'|'center'|'right'} [props.align='left'] - Horizontal alignment of the tab bar within its container.
 * @param {boolean}  [props.fullWidth=false] - When true, tabs stretch equally to fill the container width.
 * @param {string[]} [props.disabled=[]] - Array of tab `key`s that should be non-interactive and visually dimmed.
 * @param {string}   [props.className=''] - Additional CSS classes applied to the outer tablist container.
 * @param {string}   [props.tabClassName=''] - Additional CSS classes applied to each individual tab button.
 * @param {function} [props.renderTab] - Custom render function for tab content. Signature: `(tab, isActive, isDisabled) => ReactNode`. When provided, overrides the default label rendering while preserving accessibility and click handling.
 */
function TabsV2({
  tabs = [],
  activeKey,
  onChange,
  variant = 'line',
  size = 'md',
  align = 'left',
  fullWidth = false,
  disabled = [],
  className = '',
  tabClassName = '',
  renderTab,
}) {
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.line;

  return (
    <div
      role='tablist'
      className={`tw-flex tw-items-center ${styles.container} ${ALIGN_STYLES[align]} ${className}`}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === activeKey;
        const isDisabled = disabled.includes(tab.key);

        const tabStyle = getTabStyle(variant, isActive);

        if (renderTab) {
          return (
            <button
              key={tab.key}
              role='tab'
              type='button'
              style={getRenderTabStyle()}
              aria-selected={isActive}
              disabled={isDisabled}
              onClick={() => onChange?.(tab.key)}
            >
              {renderTab(tab, isActive, isDisabled)}
            </button>
          );
        }

        return (
          <button
            key={tab.key}
            role='tab'
            type='button'
            style={tabStyle}
            aria-selected={isActive}
            disabled={isDisabled}
            onClick={() => onChange?.(tab.key)}
            className={`
              tw-font-medium tw-transition-all tw-duration-200
              ${SIZE_STYLES[size]}
              ${fullWidth ? 'tw-flex-1 tw-text-center' : ''}
              ${isDisabled ? 'tw-opacity-40 !tw-cursor-not-allowed' : ''}
              ${tabClassName}
            `}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

TabsV2.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  activeKey: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['line', 'pill', 'enclosed']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  align: PropTypes.oneOf(['left', 'center', 'right']),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  tabClassName: PropTypes.string,
  renderTab: PropTypes.func,
};

export default TabsV2;
